<script setup lang="ts">
import { DataTable } from '@metapic-apps/shared'
import { ref, toRef, watch } from 'vue'

import { useGetLinkClicks, useRecalculateLinkClicks } from '@/api/link-clicks.api'
import { notification } from '@/lib/helpers'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  linkId: number
}>()

const tagIdRef = toRef(props, 'linkId')
const { data, isLoading, refresh } = useGetLinkClicks(tagIdRef)
const { recalculate, isLoading: isSaving } = useRecalculateLinkClicks()

const percentInput = ref('0')
const percentInitialized = ref(false)

watch(
  data,
  (val) => {
    if (val && !percentInitialized.value) {
      percentInput.value = String(val.percent)
      percentInitialized.value = true
    }
  },
  { immediate: true },
)

const onSave = async () => {
  const result = await recalculate(props.linkId, Number(percentInput.value))
  if (!result) {
    return
  }
  await refresh()
  notification.success('Click report recalculated')
}
</script>

<template>
  <LoadingComponent v-if="isLoading" :size="48" />

  <FlexComponent v-else-if="data" direction="column" :gap="32">
    <DataTable class="click-report-table">
      <template #header>
        <th scope="col">Type</th>
        <th scope="col" class="click-report-table__metric">Clicks</th>
      </template>
      <tr v-for="click in data.link_clicks" :key="click.id">
        <td>{{ click.name }}</td>
        <td class="click-report-table__metric">{{ click.count }}</td>
      </tr>
    </DataTable>

    <BoxComponent class="remove-section">
      <FlexComponent direction="column" :gap="8">
        <TypographyComponent variant="title-large">Remove clicks (%)</TypographyComponent>
        <FlexComponent align="center" :gap="16">
          <FlexComponent flex="1" class="percent-input">
            <InputComponent v-model="percentInput" type="number">
              <template #suffix>
                <TypographyComponent variant="title-large">%</TypographyComponent>
              </template>
            </InputComponent>
          </FlexComponent>
          <ButtonComponent variant="primary" :is-loading="isSaving" @click="onSave">Save</ButtonComponent>
        </FlexComponent>
      </FlexComponent>
    </BoxComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.percent-input {
  :deep(> div),
  :deep(.el-input) {
    width: 100%;
  }
}

.click-report-table :deep(.click-report-table__metric) {
  width: 96px;
  text-align: right;
}

.table-head {
  padding: 0 16px 8px;
  align-items: center;
  gap: 16px;

  &__report {
    flex: 1;
  }

  &__clicks {
    width: 96px;
    text-align: right;
  }
}

.click-row {
  padding: 16px;
  background: var(--inverted);
  border: 1px solid rgba(34, 34, 34, 0.08);
  border-radius: 16px;

  &__name {
    flex: 1;
  }

  &__count {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 96px;
    height: 56px;
    background: rgba(34, 34, 34, 0.02);
    border: 1px solid rgba(34, 34, 34, 0.12);
    border-radius: 8px;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
  }
}

.remove-section {
  padding: 24px;
  background: rgba(34, 34, 34, 0.04);
  border-radius: 16px;
}
</style>
